import { useState, useEffect } from 'react';

import { useUserContext } from '~/utilities/context/dynamic/UserContext';
import { useAppContext } from '~/utilities/context/static/AppContext';
import { useEvaluatePersonalisedDiscountLazyQuery } from '~/utilities/graphql/codegen';

interface UseEvaluatePersonalisedDiscountParams {
  productCode?: string | null;
}

interface UseEvaluatePersonalisedDiscountReturn {
  isValidDiscount: boolean | null;
  isLoading: boolean;
}

const isDiscountExpired = (expirationDate: string): boolean =>
  new Date(expirationDate).getTime() < Date.now();

export const useEvaluatePersonalisedDiscount = ({
  productCode,
}: UseEvaluatePersonalisedDiscountParams): UseEvaluatePersonalisedDiscountReturn => {
  const { user } = useUserContext();
  const { siteType } = useAppContext();
  const [isValidDiscount, setIsValidDiscount] = useState<boolean | null>(null);

  const [evaluateDiscount, { loading, data, error }] = useEvaluatePersonalisedDiscountLazyQuery({
    fetchPolicy: 'no-cache',
  });

  useEffect(() => {
    if (error) {
      setIsValidDiscount(false);

      return;
    }

    if (!data) {
      return;
    }

    const expirationDate = data?.evaluatePersonalisedDiscount?.expirationDate;

    if (!expirationDate || isDiscountExpired(expirationDate)) {
      setIsValidDiscount(false);
    } else {
      setIsValidDiscount(true);
    }
  }, [data, error]);

  useEffect(() => {
    const expirationDate = user?.personalisedDiscount?.expirationDate;

    if (!expirationDate || !productCode) {
      return;
    }

    if (isDiscountExpired(expirationDate)) {
      setIsValidDiscount(false);

      return;
    }

    setIsValidDiscount(null);

    evaluateDiscount({
      variables: {
        siteType,
        productCode,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.personalisedDiscount?.expirationDate, productCode, siteType]);

  return {
    isValidDiscount,
    isLoading: loading,
  };
};
